import React from "react";

/**
 * A queue size for a tab, as a pill after the label (`Join requests · 2`).
 *
 * <p>Mirrors `SectionCountBadge` in `DiscussionPageSectionControls` — the tab precedent this whole
 * row follows — rather than inventing a second badge shape for the same job.
 *
 * <p><b>Zero renders nothing.</b> Not a dimmed `0`, not an empty pill: the ABSENCE of a badge is what
 * a leader reads as "this queue is empty", and a `0` says the same thing in a way that still demands
 * to be looked at.
 *
 * <p>Its own module since `GROUP-82` (S7 U18): the console's landing index renders the same pill, and a
 * second copy of the tone table would drift from this one.
 *
 * <p>A capped variant (`99+`) existed for the board tab's unread count and went with it — no caller
 * produces a capped count any more, and an unproduced branch is an untested branch.
 */
export function GroupTabCountBadge({ count, active, tone = "neutral", dataPlaywrightId }: {
    count?: number;
    active: boolean;
    /**
     * `owed` (`GROUP-66`, A29): work waiting on the VIEWER'S decision wears the header bell's own tokens,
     * the product's one "someone is waiting on you" colour, so it is not the quietest thing on the
     * owner's screen. Neutral counts (items in a list) keep the muted chip.
     */
    tone?: "neutral" | "owed";
    dataPlaywrightId?: string;
}) {
    if (typeof count !== "number" || !Number.isFinite(count) || count <= 0) {
        return null;
    }
    const toneClassName = tone === "owed"
        ? "bg-[var(--error)] text-[var(--destructive-foreground)]"
        : active ? "bg-primary/10 text-foreground" : "bg-muted text-muted-foreground";
    return (
        <span
            aria-hidden="true"
            data-playwright-id={dataPlaywrightId}
            className={`rounded-full px-1.5 py-0.5 text-[0.6875rem] font-semibold leading-none tabular-nums ${toneClassName}`}
        >
            {String(count)}
        </span>
    );
}
